import { dayKey, formatMoney, netPnl, tradingDayKey, currentTradingDay } from './pnl';
import { tagKind } from './tags';
import { cashFlowAmount } from './balance';
import { translateNow as tn } from './langState';

const DAY_NAMES = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

export function performanceStats(trades) {
  const nets = trades.map(netPnl);
  const winners = nets.filter((n) => n > 0);
  const losers = nets.filter((n) => n < 0);
  const grossWin = winners.reduce((a, b) => a + b, 0);
  const grossLoss = -losers.reduce((a, b) => a + b, 0);
  const today = currentTradingDay();
  const weekStart = new Date();
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  return {
    count: trades.length,
    net: nets.reduce((a, b) => a + b, 0),
    winRate: trades.length ? (winners.length / trades.length) * 100 : null,
    avgWin: winners.length ? grossWin / winners.length : null,
    avgLoss: losers.length ? grossLoss / losers.length : null,
    // Gross profit ÷ gross loss; Infinity when there are wins and no losses.
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : null,
    today: trades.filter((t) => tradingDayKey(t.date) === today).reduce((s, t) => s + netPnl(t), 0),
    week: trades.filter((t) => new Date(t.date) >= weekStart).reduce((s, t) => s + netPnl(t), 0),
  };
}

// One point per trading day (a day with trades) for the last `days` of them: the running
// total at the end of that day. With a starting balance it's the account balance, otherwise
// cumulative net P&L (including everything before the window).
// `cashFlows` (deposits and withdrawals) move the balance line up to that day, but `dayNet` stays trading P&L.
// With a single trading day the line starts from the total just before it.
export function equitySeries(trades, startingBalance, days = 30, cashFlows = []) {
  const byDay = {};
  for (const t of trades) byDay[tradingDayKey(t.date)] = (byDay[tradingDayKey(t.date)] || 0) + netPnl(t);
  const cash = (cashFlows || []).map((f) => [f.date, cashFlowAmount(f)]);
  const keys = Object.keys(byDay).sort().slice(-days);
  const upTo = (key, strict) => (startingBalance || 0)
    + [...Object.entries(byDay), ...cash].reduce((s, [k, v]) => ((strict ? k < key : k <= key) ? s + v : s), 0);
  const at = (key) => new Date(`${key}T12:00:00`);
  if (!keys.length) {
    const today = dayKey(new Date());
    const value = upTo(today, false);
    return [{ key: today, date: at(today), value, dayNet: 0 }, { key: today, date: at(today), value, dayNet: 0 }];
  }
  const points = keys.map((key) => ({ key, date: at(key), value: upTo(key, false), dayNet: byDay[key] }));
  if (points.length === 1) points.unshift({ key: `${keys[0]}-before`, date: at(keys[0]), value: upTo(keys[0], true), dayNet: 0 });
  return points;
}

// Trades with neither tags nor notes, newest first.
export function unjournaledTrades(trades) {
  return trades
    .filter((t) => !(t.tags && t.tags.length) && !(t.notes && t.notes.trim()))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
}

function hourLabel(h) {
  const suffix = tn(h < 12 ? 'AM' : 'PM');
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:00 ${suffix}`;
}

// The single most useful pattern in the last 30 days of trades, or null.
// Each check needs a minimum sample and scores by dollars at stake; the biggest wins.
// With `recent` false it looks at every trade it's given (already narrowed by the trader's
// date range and filters).
export function topInsight(allTrades, library, recent = true) {
  const cutoff = recent ? Date.now() - 30 * 24 * 60 * 60 * 1000 : 0;
  const trades = allTrades.filter((t) => new Date(t.date).getTime() >= cutoff);
  if (trades.length < 10) return null;
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  const totalLoss = -sorted.map(netPnl).filter((n) => n < 0).reduce((a, b) => a + b, 0);
  const overallWin = sorted.filter((t) => netPnl(t) > 0).length / sorted.length;
  const candidates = [];

  // 1. An hour of the day where most of the losses happen.
  const byHour = {};
  for (const t of sorted) {
    if (t.timeUnknown) continue;
    const h = new Date(t.date).getHours();
    if (!byHour[h]) byHour[h] = { net: 0, count: 0, loss: 0 };
    byHour[h].net += netPnl(t);
    byHour[h].count += 1;
    if (netPnl(t) < 0) byHour[h].loss -= netPnl(t);
  }
  for (const [h, b] of Object.entries(byHour)) {
    if (b.count >= 3 && b.net < 0 && totalLoss > 0 && b.loss / totalLoss >= 0.4) {
      candidates.push({
        score: -b.net,
        sample: b.count,
        kind: 'time',
        text: tn('{pct}% of your losses in the last 30 days came between {from} and {to} ({amount} over {n} trades).',
          { pct: Math.round((b.loss / totalLoss) * 100), from: hourLabel(+h), to: hourLabel(+h + 1), amount: formatMoney(b.net), n: b.count }),
        tip: tn('Consider sitting out that hour, or trading it with smaller size.'),
        cost: b.net, rule: tn('No trades between {from} and {to}.', { from: hourLabel(+h), to: hourLabel(+h + 1) }),
      });
    }
  }

  // 2. The next trade on the same day after a losing trade.
  const afterLoss = [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    if (netPnl(prev) < 0 && tradingDayKey(prev.date) === tradingDayKey(sorted[i].date)) afterLoss.push(sorted[i]);
  }
  if (afterLoss.length >= 3) {
    const net = afterLoss.reduce((s, t) => s + netPnl(t), 0);
    const winRate = afterLoss.filter((t) => netPnl(t) > 0).length / afterLoss.length;
    if (net < 0 && winRate < overallWin - 0.1) {
      candidates.push({
        score: -net,
        sample: afterLoss.length,
        kind: 'tilt',
        text: tn('After a losing trade, your next trade that day won only {rate}% of the time (vs {overall}% overall): {amount} over {n} trades.',
          { rate: Math.round(winRate * 100), overall: Math.round(overallWin * 100), amount: formatMoney(net), n: afterLoss.length }),
        tip: tn('Try a 15-minute break after every loss before taking the next trade.'),
        cost: net, rule: tn('After a losing trade, I wait 15 minutes before the next one.'),
        compare: { label: tn('Next trade after a loss'), value: Math.round(winRate * 100), otherLabel: tn('All your trades'), other: Math.round(overallWin * 100) },
      });
    }
  }

  // 3. The costliest mistake tag.
  const byTag = {};
  for (const t of sorted) {
    for (const name of t.tags || []) {
      if (tagKind(name, library) !== 'mistake') continue;
      if (!byTag[name]) byTag[name] = { net: 0, count: 0 };
      byTag[name].net += netPnl(t);
      byTag[name].count += 1;
    }
  }
  for (const [name, b] of Object.entries(byTag)) {
    if (b.count >= 2 && b.net < 0) {
      candidates.push({
        score: -b.net,
        sample: b.count,
        kind: 'tag',
        text: tn('"{tag}" trades cost you {amount} over {n} trades in the last 30 days.', { tag: name, amount: formatMoney(b.net), n: b.count }),
        tip: tn('Write a rule in your trade plan that stops this before it happens.'),
        cost: b.net, rule: tn('Before every trade, I check it isn’t a "{tag}" trade.', { tag: name }),
      });
    }
  }

  // 4. A weekday that loses money.
  const byWeekday = {};
  for (const t of sorted) {
    const d = new Date(t.date).getDay();
    if (!byWeekday[d]) byWeekday[d] = { net: 0, count: 0 };
    byWeekday[d].net += netPnl(t);
    byWeekday[d].count += 1;
  }
  for (const [d, b] of Object.entries(byWeekday)) {
    if (b.count >= 4 && b.net < 0) {
      candidates.push({
        score: -b.net * 0.8,
        sample: b.count,
        kind: 'weekday',
        text: tn('{days} are your weakest day: {amount} over {n} trades in the last 30 days.', { days: tn(DAY_NAMES[d]), amount: formatMoney(b.net), n: b.count }),
        tip: tn('Review your trades from {onDays}; consider trading smaller on that day.', { onDays: tn(`on ${DAY_NAMES[d]}`) }),
        cost: b.net, rule: tn('On {days} I trade half my usual size.', { days: tn(DAY_NAMES[d]) }),
      });
    }
  }

  // 5. Days with many trades doing worse than quieter days.
  const tradesPerDay = {};
  for (const t of sorted) {
    const k = tradingDayKey(t.date);
    if (!tradesPerDay[k]) tradesPerDay[k] = { net: 0, count: 0 };
    tradesPerDay[k].net += netPnl(t);
    tradesPerDay[k].count += 1;
  }
  const busy = Object.values(tradesPerDay).filter((d) => d.count >= 5);
  if (busy.length >= 2) {
    const busyNet = busy.reduce((s, d) => s + d.net, 0);
    if (busyNet < 0) {
      candidates.push({
        score: -busyNet,
        sample: busy.length * 5,
        kind: 'overtrading',
        text: tn('On days with 5 or more trades you lost {amount} across {n} days.', { amount: formatMoney(busyNet), n: busy.length }),
        tip: tn('Set a maximum number of trades per day in your plan.'),
        cost: busyNet, rule: tn('No more than 4 trades a day.'),
      });
    }
  }

  if (!candidates.length) return { kind: 'none', text: tn('No losing patterns stand out in your last 30 days. Keep tagging trades to sharpen these insights.') };
  return candidates.sort((a, b) => b.score - a.score)[0];
}
